import "@/App.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import IntroOverlay from "@/components/IntroOverlay";
import ScrollProgress from "@/components/ScrollProgress";
import BackToTop from "@/components/BackToTop";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Problem from "@/pages/Problem";
import Solution from "@/pages/Solution";
import Platform from "@/pages/Platform";
import Partners from "@/pages/Partners";
import Founder from "@/pages/Founder";
import Contact from "@/pages/Contact";
import Experience from "@/pages/Experience";
import Impact from "@/pages/Impact";
import Sources from "@/pages/Sources";
import Community from "@/pages/Community";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/NotFound";
import { Signup, Login, Settings } from "@/pages/UserAuth";
import Discovery from "@/pages/Discovery";
import InviteAccept from "@/pages/InviteAccept";
import { AdminHome, AdminOrganizations, AdminInvitations } from "@/pages/AdminWorkspace";
import AdminGovernment from "@/pages/admin/AdminGovernment";
import AdminImport from "@/pages/admin/AdminImport";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSnapshots from "@/pages/admin/AdminSnapshots";
import Privacy from "@/pages/Privacy";
import GuideWorkspace from "@/pages/GuideWorkspace";
import ProviderDashboard from "@/pages/ProviderDashboard";
import MySupport from "@/pages/MySupport";
import { UserAuthProvider } from "@/context/UserAuthContext";
import V2Home from "@/pages/v2/V2Home";
import V2About from "@/pages/v2/V2About";
import V2Approach from "@/pages/v2/V2Approach";
import V2Partners from "@/pages/v2/V2Partners";
import V2Layout from "@/pages/v2/V2Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Fetch public feature flags once per app load. */
function useOpsEnabled() {
  const [ops, setOps] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/config`)
      .then((r) => live && setOps(!!r.data.operational_features))
      .catch(() => live && setOps(false));
    return () => { live = false; };
  }, []);
  return ops;
}

/**
 * Server-enforced isolation is the real control (operational APIs return 404).
 * This gate handles presentation: pilot workspaces show an unavailable notice
 * instead of a broken dashboard when operational features are isolated.
 */
function OpsGate({ children }) {
  const ops = useOpsEnabled();
  if (ops === null) return null;
  if (!ops) {
    return (
      <div className="max-w-xl mx-auto my-24 px-6 text-center" data-testid="ops-disabled-notice">
        <p className="eyebrow text-gold-deep">Pilot workspace</p>
        <h1 className="mt-3 font-display font-black text-navy text-2xl">This pilot workspace is not available here.</h1>
        <p className="mt-4 text-slateSoft leading-relaxed">
          WHO&rsquo;s coordinated-services workspaces are not enabled in this environment.
          This public website presents WHO&rsquo;s mission and planned pilot — no operational
          case coordination takes place here.
        </p>
      </div>
    );
  }
  return children;
}

/** Marketing site chrome for public pages only. Admin routes render bare. */
function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/admin/organizations" element={<AdminOrganizations />} />
        <Route path="/admin/government-resources" element={<AdminGovernment />} />
        <Route path="/admin/import" element={<AdminImport />} />
        <Route path="/admin/snapshots" element={<AdminSnapshots />} />
        <Route path="/admin/users/roles" element={<OpsGate><AdminUsers /></OpsGate>} />
        <Route path="/admin/invitations" element={<OpsGate><AdminInvitations /></OpsGate>} />
        <Route path="/admin/legacy" element={<AdminDashboard />} />
      </Routes>
    );
  }

  return (
    <>
      <ScrollProgress />
      <IntroOverlay />
      <BackToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/problem" element={<Problem />} />
          <Route path="/solution" element={<Solution />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/founder" element={<Founder />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/experience" element={<Experience />} />
          <Route path="/impact" element={<Impact />} />
          <Route path="/sources" element={<Sources />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/community" element={<Community />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/settings" element={<OpsGate><Settings /></OpsGate>} />
          <Route path="/guides" element={<OpsGate><GuideWorkspace /></OpsGate>} />
          <Route path="/provider" element={<OpsGate><ProviderDashboard /></OpsGate>} />
          <Route path="/my-support" element={<OpsGate><MySupport /></OpsGate>} />
          <Route path="/discovery" element={<Discovery />} />
          <Route path="/invite/accept" element={<OpsGate><InviteAccept /></OpsGate>} />
          {/* WHO Website — Version 2 (Simplified). Self-contained chrome; V1 untouched. */}
          <Route path="/v2" element={<V2Home />} />
          <Route path="/v2/about" element={<V2About />} />
          <Route path="/v2/approach" element={<V2Approach />} />
          <Route path="/v2/partners" element={<V2Partners />} />
          <Route path="/v2/resources" element={<V2Layout><Discovery /></V2Layout>} />
          <Route path="/v2/contact" element={<V2Layout><Contact /></V2Layout>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <UserAuthProvider>
        <AppShell />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: '"IBM Plex Sans", sans-serif',
              border: "1px solid #E2E8F0",
            },
          }}
        />
      </UserAuthProvider>
    </BrowserRouter>
  );
}

export default App;
